import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Form,
    Label,
    Input,
    Button,
    Table,
} from "reactstrap"
import { connect } from "react-redux"
import { useFormik } from "formik"
import * as Yup from "yup"
import Select from "react-select"
import {
    setBreadcrumbItems,
    getCustomers,
    getProducts,
    addNewQuote,
} from "../../store/actions"

const QuoteCreate = props => {
    const navigate = useNavigate()
    document.title = "Créer un Devis | CRM Entreprise"

    const breadcrumbItems = [
        { title: "Finances", link: "#" },
        { title: "Devis", link: "/finance/quotes" },
        { title: "Nouveau", link: "#" },
    ]

    const customerData = props.customers?.data || props.customers || []
    const productData = props.products?.data || props.products || []

    useEffect(() => {
        props.setBreadcrumbItems("Nouveau Devis", breadcrumbItems)
        if (!customerData || customerData.length === 0) props.getCustomers()
        if (!productData || productData.length === 0) props.getProducts()
    }, [])

    const [items, setItems] = useState([
        { id: Date.now(), product_id: null, description: "", quantity: 1, unit_price: 0, tax_rate: 20, discount_rate: 0, total: 0 }
    ])

    const customerOptions = (Array.isArray(customerData) ? customerData : []).map(c => ({
        value: c.id,
        label: `${c.first_name} ${c.last_name} (${c.company_name || 'Particulier'})`
    }))

    const productOptions = (Array.isArray(productData) ? productData : []).map(p => ({
        value: p.id,
        label: `${p.sku} - ${p.name}`,
        price: p.unit_price,
        tax: p.tax_rate,
        description: p.name
    }))

    const validation = useFormik({
        initialValues: {
            customer_id: "",
            quote_date: new Date().toISOString().split('T')[0],
            valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            notes: "",
            terms: "Ce devis est valable 30 jours.",
            discount_type: "percentage",
            discount_value: 0,
        },
        validationSchema: Yup.object({
            customer_id: Yup.string().required("Veuillez choisir un client"),
            quote_date: Yup.date().required("Veuillez saisir la date du devis"),
        }),
        onSubmit: (values) => {
            const quoteData = {
                ...values,
                items: items.map(item => ({
                    description: item.description,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                    tax_rate: item.tax_rate,
                    discount_rate: item.discount_rate,
                }))
            }
            props.addNewQuote(quoteData, navigate)
        }
    })

    const calculateTotals = (currentItems) => {
        let subtotal = 0
        let taxAmount = 0
        let itemDiscountTotal = 0

        currentItems.forEach(item => {
            const lineTotal = item.quantity * item.unit_price
            const itemDiscount = lineTotal * (item.discount_rate / 100)
            const lineAfterDiscount = lineTotal - itemDiscount
            const itemTax = lineAfterDiscount * (item.tax_rate / 100)

            subtotal += lineTotal
            itemDiscountTotal += itemDiscount
            taxAmount += itemTax
        })

        // Global discount calculation
        let globalDiscount = 0
        const discValue = parseFloat(validation.values.discount_value) || 0
        if (validation.values.discount_type === 'percentage') {
            globalDiscount = (subtotal - itemDiscountTotal) * (discValue / 100)
        } else {
            globalDiscount = discValue
        }

        const finalDiscountAmount = itemDiscountTotal + globalDiscount

        return {
            subtotal,
            tax_amount: taxAmount,
            discount_amount: finalDiscountAmount,
            global_discount: globalDiscount,
            total: subtotal - itemDiscountTotal - globalDiscount + taxAmount
        }
    }

    const totals = calculateTotals(items)

    const addItem = () => {
        setItems([...items, { id: Date.now(), product_id: null, description: "", quantity: 1, unit_price: 0, tax_rate: 20, discount_rate: 0, total: 0 }])
    }

    const removeItem = (id) => {
        if (items.length > 1) {
            setItems(items.filter(item => item.id !== id))
        }
    }

    const handleItemChange = (id, field, value) => {
        const newItems = items.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value }

                // If product selected, auto-fill price and tax
                if (field === 'product_id' && value) {
                    const product = (Array.isArray(productData) ? productData : []).find(p => p.id === value)
                    if (product) {
                        updatedItem.unit_price = parseFloat(product.unit_price) || 0
                        updatedItem.tax_rate = parseFloat(product.tax_rate) || 0
                        updatedItem.description = product.name
                    }
                }

                // Recalculate line total
                const lineTotal = updatedItem.quantity * updatedItem.unit_price
                const discount = lineTotal * (updatedItem.discount_rate / 100)
                const tax = (lineTotal - discount) * (updatedItem.tax_rate / 100)
                updatedItem.total = lineTotal - discount + tax

                return updatedItem
            }
            return item
        })
        setItems(newItems)
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(amount)
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                    <Row>
                        <Col lg={8}>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 mb-4">Détails du Devis</CardTitle>

                                    <Row>
                                        <Col md={12}>
                                            <div className="mb-4">
                                                <Label>Client <span className="text-danger">*</span></Label>
                                                <Select
                                                    options={customerOptions}
                                                    placeholder="Rechercher un client..."
                                                    classNamePrefix="select2-selection"
                                                    onChange={(option) => validation.setFieldValue("customer_id", option ? option.value : "")}
                                                />
                                                {validation.errors.customer_id && validation.touched.customer_id && (
                                                    <div className="text-danger mt-1">{validation.errors.customer_id}</div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <div className="mb-3">
                                                <Label>Date du Devis <span className="text-danger">*</span></Label>
                                                <Input
                                                    name="quote_date"
                                                    type="date"
                                                    onChange={validation.handleChange}
                                                    value={validation.values.quote_date}
                                                />
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="mb-3">
                                                <Label>Valable jusqu'au</Label>
                                                <Input
                                                    name="valid_until"
                                                    type="date"
                                                    onChange={validation.handleChange}
                                                    value={validation.values.valid_until}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h5 className="mb-0">Articles & Services</h5>
                                        <Button color="light" type="button" onClick={addItem}>
                                            <i className="mdi mdi-plus me-1 text-primary"></i> Ajouter une ligne
                                        </Button>
                                    </div>

                                    <div className="table-responsive">
                                        <Table className="table-centered table-nowrap mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '25%' }}>Produit / Service</th>
                                                    <th style={{ width: '10%' }}>Qté</th>
                                                    <th style={{ width: '15%' }}>Prix Unitaire</th>
                                                    <th style={{ width: '15%' }}>TVA %</th>
                                                    <th style={{ width: '20%' }}>Total HT</th>
                                                    <th style={{ width: '5%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <Select
                                                                options={productOptions}
                                                                placeholder="Choisir..."
                                                                onChange={(opt) => handleItemChange(item.id, 'product_id', opt ? opt.value : null)}
                                                                isClearable
                                                                className="mb-2"
                                                            />
                                                            <Input
                                                                type="text"
                                                                placeholder="Description additionnelle..."
                                                                value={item.description}
                                                                onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                value={item.quantity}
                                                                onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                value={item.unit_price}
                                                                onChange={(e) => handleItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="select"
                                                                value={item.tax_rate}
                                                                onChange={(e) => handleItemChange(item.id, 'tax_rate', parseFloat(e.target.value) || 0)}
                                                            >
                                                                <option value="0">0%</option>
                                                                <option value="7">7%</option>
                                                                <option value="10">10%</option>
                                                                <option value="14">14%</option>
                                                                <option value="20">20%</option>
                                                            </Input>
                                                        </td>
                                                        <td className="fw-bold">
                                                            {formatCurrency(item.quantity * item.unit_price)}
                                                        </td>
                                                        <td>
                                                            <Button color="light" size="sm" className="text-danger border-0" onClick={() => removeItem(item.id)}>
                                                                <i className="mdi mdi-delete font-size-16"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>

                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md={6}>
                                            <div className="mb-3">
                                                <Label>Notes</Label>
                                                <Input
                                                    name="notes"
                                                    type="textarea"
                                                    rows="3"
                                                    placeholder="Notes à afficher sur le devis..."
                                                    onChange={validation.handleChange}
                                                    value={validation.values.notes}
                                                />
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="mb-3">
                                                <Label>Conditions Générales</Label>
                                                <Input
                                                    name="terms"
                                                    type="textarea"
                                                    rows="3"
                                                    onChange={validation.handleChange}
                                                    value={validation.values.terms}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 mb-4">Récapitulatif Financier</CardTitle>

                                    <div className="mb-4 pt-2 border-top">
                                        <Label className="form-label font-size-13 text-muted">Remise Globale (Facultatif)</Label>
                                        <Row className="g-2">
                                            <Col xs={4}>
                                                <Input
                                                    name="discount_type"
                                                    type="select"
                                                    className="form-select"
                                                    onChange={validation.handleChange}
                                                    value={validation.values.discount_type}
                                                >
                                                    <option value="percentage">%</option>
                                                    <option value="fixed">MAD</option>
                                                </Input>
                                            </Col>
                                            <Col xs={8}>
                                                <Input
                                                    name="discount_value"
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Valeur..."
                                                    onChange={validation.handleChange}
                                                    value={validation.values.discount_value}
                                                />
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Total H.T</span>
                                        <span className="fw-bold">{formatCurrency(totals.subtotal)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Remise Totale</span>
                                        <span className="text-danger">-{formatCurrency(totals.discount_amount)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                                        <span className="text-muted">TVA Totale</span>
                                        <span className="fw-bold">{formatCurrency(totals.tax_amount)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-4 mt-2">
                                        <h4 className="mb-0">Total TTC</h4>
                                        <h4 className="mb-0 text-primary">{formatCurrency(totals.total)}</h4>
                                    </div>

                                    <div className="d-grid gap-2">
                                        <Button color="primary" type="submit" size="lg">
                                            <i className="mdi mdi-content-save me-1"></i> Enregistrer le Devis
                                        </Button>
                                        <Button color="light" type="button" size="lg" onClick={() => navigate("/finance/quotes")}>
                                            Annuler
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    customers: state.Customers.customers,
    products: state.Products.products,
    loading: state.Quotes.loading,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getCustomers,
    getProducts,
    addNewQuote,
})(QuoteCreate)
